import { useEffect, useState } from "react";
import api from "../lib/api";
import { Footer, Nav } from "./Layout";
import { COMPANY, CONTACT_EMAIL, PRODUCT } from "./content";

/* Security, DPA, Status and Changelog: the trust pages the footer links to
   (PRD 22). Every claim here describes what the product does today. */

const Shell = ({ eyebrow, title, intro, children }) => {
  useEffect(() => { window.scrollTo(0, 0); }, [title]);
  return (
    <div className="min-h-screen bg-ac-bg text-ac-text">
      <Nav signupsOpen={false} signedIn={false} />
      <main className="mx-auto max-w-3xl px-4 sm:px-8 pt-32 pb-24">
        <p className="mono-label text-ac-link">{eyebrow}</p>
        <h1 className="mt-3 font-display text-[44px] sm:text-[56px] font-semibold leading-[1.05]">{title}</h1>
        {intro && <p className="mt-4 text-[17px] leading-relaxed text-ac-muted">{intro}</p>}
        <div className="mt-12 space-y-10">{children}</div>
      </main>
      <Footer />
    </div>
  );
};

const Block = ({ title, children }) => (
  <section>
    <h2 className="font-display text-[22px] font-semibold">{title}</h2>
    <div className="mt-3 space-y-3 text-[16px] leading-relaxed text-ac-muted">{children}</div>
  </section>
);

export const Security = () => (
  <Shell eyebrow="Trust" title="Security"
    intro={`How ${PRODUCT} protects your workspace, your brand and the accounts you connect.`}>
    <Block title="Workspace isolation">
      <p>Every query for customer data is scoped to a workspace before it runs. Code that tries to read
        posts, accounts, feeds or settings without a workspace is refused, so one customer's data cannot
        be read in another's session.</p>
    </Block>
    <Block title="Sign-in">
      <p>Passwords are stored only as bcrypt hashes. Every credential check is rate limited per address,
        and sessions are signed tokens that can be revoked.</p>
    </Block>
    <Block title="Connected accounts">
      <p>LinkedIn, Meta and Threads are connected through their official sign-in screens with the
        narrowest permissions publishing needs. You can disconnect any account in the app or revoke
        access from the platform's own settings at any time. App secrets and WordPress application
        passwords are never shown back in the app once saved.</p>
    </Block>
    <Block title="Your content">
      <p>Your content and brand data are never used to train shared models. Images are served through
        signed links that expire. Approval messages on Telegram and WhatsApp carry the post preview and
        the reason it was held, never credentials.</p>
    </Block>
    <Block title="Nothing ships silently">
      <p>Every post runs the hard checks before it can be scheduled. A post that fails is held with the
        reason; nothing is silently dropped or silently published. Approvals and rejections are recorded
        in an audit log with who made them.</p>
    </Block>
    <Block title="Reporting a vulnerability">
      <p>Email <a className="text-ac-link underline" href={`mailto:${CONTACT_EMAIL}?subject=Security`}>{CONTACT_EMAIL}</a> with
        the subject "Security". We acknowledge reports within two working days.</p>
    </Block>
  </Shell>
);

export const Dpa = () => (
  <Shell eyebrow="Trust" title="Data processing agreement"
    intro={`${COMPANY} acts as your processor for the content and account data you put into ${PRODUCT}.`}>
    <Block title="Requesting a DPA">
      <p>Email <a className="text-ac-link underline" href={`mailto:${CONTACT_EMAIL}?subject=DPA%20request`}>{CONTACT_EMAIL}</a> with
        the subject "DPA request", your company's legal name and address. We send our standard agreement
        for signature, covering GDPR, UK GDPR, CCPA and India's DPDP Act.</p>
    </Block>
    <Block title="What it covers">
      <p>The data we process on your instruction, the purposes, our sub-processors (hosting, AI model and
        image providers, and the social platforms you publish to), security measures, breach notification,
        and the return or deletion of your data within 30 days of your request or the end of your contract.</p>
    </Block>
    <Block title="Data deletion">
      <p>To have your data deleted, email us from the address on your account with the subject "Delete my
        data". The steps are also set out in our <a className="text-ac-link underline" href="/privacy">privacy policy</a>.</p>
    </Block>
  </Shell>
);

export const Status = () => {
  const [state, setState] = useState(null);
  const [at, setAt] = useState(null);
  const check = async () => {
    try {
      const { data } = await api.get("/health");
      setState({ api: data.api === "ok", database: data.database === "ok", detail: data.database });
    } catch {
      setState({ api: false, database: false });
    }
    setAt(new Date());
  };
  useEffect(() => {
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, []);
  const ok = state?.api && state?.database;
  const Row = ({ name, up }) => (
    <li className="flex items-center justify-between py-3">
      <span className="text-ac-text">{name}</span>
      <span className={`inline-flex items-center gap-2 font-mono text-[13px] ${up ? "text-[#86EFAC]" : state ? "text-[#FCA5A5]" : "text-ac-muted"}`}>
        <span className={`w-2 h-2 rounded-full ${up ? "bg-[#4ADE80]" : state ? "bg-[#F87171]" : "bg-ac-border"}`} />
        {state ? (up ? "Operational" : "Down") : "Checking"}
      </span>
    </li>
  );
  return (
    <Shell eyebrow="Trust" title="Status" intro="Live from the service itself. Checked every 30 seconds while this page is open.">
      <div className={`rounded-[10px] border p-4 ${!state ? "border-ac-border bg-ac-surface"
        : ok ? "border-[#1A4D30] bg-[#0F2A1B]" : "border-[#6B2121] bg-[#3A1414]"}`}>
        <p className={`text-[17px] font-medium ${!state ? "text-ac-muted" : ok ? "text-[#86EFAC]" : "text-[#FCA5A5]"}`}>
          {!state ? "Checking…" : ok ? "All systems operational" : "Something is wrong. We are on it."}
        </p>
        {at && <p className="mt-1 font-mono text-[12px] text-ac-muted">Last checked {at.toLocaleTimeString()}</p>}
      </div>
      <ul className="divide-y divide-ac-border border-y border-ac-border">
        <Row name="App and API" up={state?.api} />
        <Row name="Database" up={state?.database} />
      </ul>
      <p className="text-[14px] text-ac-muted">
        Problems with LinkedIn, Meta or WordPress themselves show on each post's publishing status in the app.
        Incidents: <a className="text-ac-link underline" href={`mailto:${CONTACT_EMAIL}?subject=Status`}>{CONTACT_EMAIL}</a>.
      </p>
    </Shell>
  );
};

const RELEASES = [
  ["20 Oct 2026", "The quality gate, in every mode", [
    "Every post now reaches the queue with its hard checks run, whatever the approval mode.",
    "Brands take banned words and blocked topics.",
    "Editorial review scores voice, clarity, accuracy and visual.",
    "Failed publishes retry three times before they are reported.",
    "The redesigned site and approvals console."]],
  ["9 Oct 2026", "AdSense-safe mode and a generation pause", [
    "Stories on restricted topics are skipped before anything is written.",
    "Check a WordPress site's published posts and move risky ones to draft.",
    "Pause articles, social posts or everything without turning feeds off."]],
  ["2 Oct 2026", "Threads, LinkedIn Pages and one-step connections", [
    "Publish to Threads and to LinkedIn company pages.",
    "Connect a LinkedIn profile and its pages in one sign-in.",
    "Publish to every ticked platform; cancel all schedules at once."]],
  ["1 Oct 2026", "Teams, the engine and the gate", [
    "Workspaces, team invites and plans.",
    "The autonomous engine keeps the calendar full to a weekly target.",
    "Auto and quality approval modes; Telegram one-tap connect; bulk approval.",
    "Real engagement numbers from the platforms."]],
  ["24 Sep 2026", "Feeds and articles", [
    "News feeds, the viral news card, and full WordPress articles from the same feed.",
    "Brand logos and footer text on every image.",
    "Carousels up to 15 slides."]],
];

export const Changelog = () => (
  <Shell eyebrow="Trust" title="Changelog" intro={`What changed in ${PRODUCT}, newest first.`}>
    <ol className="space-y-10">
      {RELEASES.map(([date, title, items]) => (
        <li key={date} className="grid gap-2 sm:grid-cols-[140px_1fr]">
          <p className="font-mono text-[13px] text-ac-muted pt-1">{date}</p>
          <div>
            <h2 className="font-display text-[22px] font-semibold">{title}</h2>
            <ul className="mt-3 space-y-1.5 text-[16px] text-ac-muted list-disc pl-5">
              {items.map((i) => <li key={i}>{i}</li>)}
            </ul>
          </div>
        </li>
      ))}
    </ol>
  </Shell>
);
